import React, { useState } from 'react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { DownloadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

import { useBillingList } from '../../../hooks/useBilling';

const statusStyles: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  GENERATED: 'bg-green-100 text-green-700',
  PAID: 'bg-blue-100 text-blue-700',
  FAILED: 'bg-red-100 text-red-700',
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = user?.roleName === 'ADMIN';
  const [search, setSearch] = useState('');
  
  const { data: billingData = [], isLoading } = useBillingList(user?.userId || user?.id);
  
  const filteredInvoices = billingData.filter(
    (b) => b.ledgerNumber?.toLowerCase().includes(search.toLowerCase()) || 
           b.date?.toLowerCase().includes(search.toLowerCase())
  );

  const generateRandomCRN = () => {
    return Math.floor(1000000000 + Math.random() * 9000000000).toString();
  };

  return (
    <div className="min-h-screen bg-background px-4 lg:px-6 py-4 lg:py-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl lg:text-3xl font-bold text-text">Invoices</h1>
          <div className="flex flex-col lg:flex-row lg:items-center text-sm text-text3 gap-1 lg:gap-2">
            View and download GST invoice again shipping and other services
          </div>
        </div>

        {/* Search + Download */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="flex gap-4 w-full lg:max-w-xl">
            <div className="w-full lg:max-w-md">
              <Input
                placeholder="Search by Tracking Number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {search && (
              <Button
                variant="outline"
                onClick={() => setSearch('')}
                className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
              >
                Reset Filters
              </Button>
            )}
          </div>
          <button className="flex items-center gap-2 rounded-lg text-white p-2 bg-teal font-medium w-fit h-10">
            <DownloadIcon />
          </button>
        </div>

        {/* Table */}
        {/* Table Section */}
        <div className="overflow-x-auto">
          <div className="space-y-3">
            {/* Detached Table Header Card */}
            <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div
                className={`grid gap-2 xl:gap-4 text-center ${admin ? '[grid-template-columns:80px_repeat(5,1fr)]' : 'grid-cols-5'}`}
              >
                {admin && <span>CRN</span>}
                <div>Date</div>
                <div>Invoice Number</div>
                <div>Service Type</div>
                <div>Invoice Amount</div>
                <div>Invoice Status</div>
              </div>
            </div>

            {/* Invoice Row Cards */}
            {isLoading ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                Loading invoices...
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                No invoices found
              </div>
            ) : (
              filteredInvoices.map((invoice, index) => (
                <div
                  key={index}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm text-center"
                >
                  <div
                    className={`grid gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-start ${admin ? '[grid-template-columns:80px_repeat(5,1fr)]' : 'grid-cols-5'}`}
                  >
                    {/* CRN */}
                    {admin && (
                      <div className="text-center font-medium text-blue-500 underline cursor-pointer">
                        {generateRandomCRN()}
                      </div>
                    )}

                    {/* Date */}
                    <div className="font-medium text-text">{invoice.date}</div>

                    {/* Invoice Number */}
                    <div>
                      <span
                        onClick={() =>
                          navigate(`/invoices/${invoice.ledgerNumber}`)
                        }
                        className="text-blue-500 underline font-medium cursor-pointer"
                      >
                        {invoice.ledgerNumber}
                      </span>
                    </div>

                    {/* Service Type */}
                    <div className="text-gray-600">Shipping Charges</div>

                    {/* Invoice Amount */}
                    <div className="font-bold text-text">
                      ₹ {Number(invoice.amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </div>

                    {/* Invoice Status */}
                    <div className="flex justify-center">
                      <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold  tracking-wider ${statusStyles[invoice.status?.toUpperCase()] || 'bg-gray-100 text-gray-700'}`}
                      >
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Bottom Padding */}
            <div className="h-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
